import { Search, SlidersHorizontal, LocateFixed, Loader2, X, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { DAY_WINDOWS } from "@/lib/geo";
import { cn } from "@/lib/utils";

const chip =
  "h-9 shrink-0 gap-1.5 rounded-full border bg-card px-3 text-xs font-semibold data-[state=on]:border-transparent data-[state=on]:bg-[#0B0D10] data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-[#0B0D10]";

const STATUS_CHIPS = [
  { value: "overdue", label: "Forfalt", dot: "bg-[rgb(var(--status-overdue))]" },
  { value: "upcoming", label: "Nærmar seg", dot: "bg-[rgb(var(--status-warn))]" },
  { value: "ok", label: "OK", dot: "bg-[rgb(var(--status-ok))]" },
  { value: "planned", label: "Planlagt", dot: "bg-[rgb(var(--status-planned))] dark:bg-slate-300" },
];

const DaysChips = ({ value, onChange, testid = "map-days" }) => (
  <ToggleGroup type="single" value={String(value)} onValueChange={(v) => v !== "" && v != null && onChange(Number(v))} className="justify-start gap-1" data-testid={testid}>
    {DAY_WINDOWS.map((w) => (
      <ToggleGroupItem key={w.value} value={String(w.value)} className={chip} data-testid={`${testid}-${w.value}`}>
        <span className="sm:hidden">{w.short}</span><span className="hidden sm:inline">{w.label}</span>
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);

const StatusChips = ({ value, onChange, testid = "map-status" }) => (
  <ToggleGroup type="multiple" value={value} onValueChange={onChange} className="justify-start gap-1" data-testid={testid}>
    {STATUS_CHIPS.map((s) => (
      <ToggleGroupItem key={s.value} value={s.value} className={chip} data-testid={`${testid}-${s.value}`}>
        <span className={cn("h-2 w-2 rounded-full", s.dot)} /> {s.label}
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);

const KindChips = ({ value, onChange, testid = "map-kind" }) => (
  <ToggleGroup type="single" value={value || "all"} onValueChange={(v) => v && onChange(v === "all" ? "" : v)} className="justify-start gap-1" data-testid={testid}>
    <ToggleGroupItem value="all" className={chip} data-testid={`${testid}-all`}>Begge</ToggleGroupItem>
    <ToggleGroupItem value="service" className={chip} data-testid={`${testid}-service`}>Service</ToggleGroupItem>
    <ToggleGroupItem value="annual" className={chip} data-testid={`${testid}-annual`}>Årskontroll</ToggleGroupItem>
  </ToggleGroup>
);

const CustomerSelect = ({ customers, value, onChange, className }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className={cn("h-9 rounded-full bg-card text-xs font-semibold", className)} data-testid="map-customer-select">
      <SelectValue placeholder="Alle kundar" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">Alle kundar</SelectItem>
      {customers.map((c) => (
        <SelectItem key={c.id} value={c.id}>{c.name}{c.lat == null ? " · (utan koordinatar)" : ""}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * MapToolbar – filter + handlingar over kartet.
 * Desktop: alt inline (to slanke rader). Mobil: dagar-chips + Filter-knapp (drawer) + Nær meg + Tur.
 */
export const MapToolbar = ({ filters, onChange, customers, summary, onLocate, locating, hasUserPos, tripMode, onToggleTrip, tripCount, filterOpen, onFilterOpen }) => {
  const activeCount = (filters.status?.length || 0) + (filters.kind ? 1 : 0) + (filters.customer ? 1 : 0) + (filters.q ? 1 : 0);
  const reset = () => onChange({ status: [], kind: "", customer: "", q: "" });

  return (
    <div className="shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80" data-testid="map-toolbar">
      {/* Rad 1 */}
      <div className="flex items-center gap-2 px-3 py-2 sm:px-4">
        <div className="no-scrollbar min-w-0 flex-1 overflow-x-auto">
          <DaysChips value={filters.days} onChange={(days) => onChange({ days })} />
        </div>
        <Button variant="outline" size="sm" className={cn("h-9 shrink-0 rounded-full lg:hidden", activeCount > 0 && "border-primary/50 bg-primary/10")} onClick={() => onFilterOpen(true)} data-testid="map-filter-button">
          <SlidersHorizontal /> Filter{activeCount > 0 ? ` · ${activeCount}` : ""}
        </Button>
        <Button variant={hasUserPos ? "secondary" : "outline"} size="sm" className={cn("h-9 shrink-0 rounded-full", hasUserPos && "border-primary/40 bg-primary/10")} onClick={onLocate} disabled={locating} data-testid="map-locate-button" aria-label="Nær meg">
          {locating ? <Loader2 className="animate-spin" /> : <LocateFixed className={hasUserPos ? "text-primary" : undefined} />}
          <span className="hidden sm:inline">Nær meg</span>
        </Button>
        <Button variant={tripMode ? "default" : "outline"} size="sm" className="h-9 shrink-0 rounded-full" onClick={onToggleTrip} data-testid="map-trip-button" aria-pressed={tripMode}>
          <Route /> <span className="hidden sm:inline">Planlegg tur</span><span className="sm:hidden">Tur</span>
          {tripCount > 0 && <span className={cn("ml-0.5 rounded-full px-1.5 text-[10px] tabular-nums", tripMode ? "bg-black/15" : "bg-muted")}>{tripCount}</span>}
        </Button>
      </div>

      {/* Rad 2 – desktop */}
      <div className="hidden items-center gap-2 px-4 pb-2 lg:flex">
        <StatusChips value={filters.status} onChange={(status) => onChange({ status })} />
        <span className="mx-1 h-5 w-px bg-border" />
        <KindChips value={filters.kind} onChange={(kind) => onChange({ kind })} />
        <span className="mx-1 h-5 w-px bg-border" />
        <CustomerSelect customers={customers} value={filters.customer} onChange={(customer) => onChange({ customer })} className="w-56" />
        <div className="relative ml-auto w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={filters.q} onChange={(e) => onChange({ q: e.target.value })} placeholder="Søk maskin, kunde, adresse …" className="h-9 rounded-full bg-card pl-8 text-xs" data-testid="map-search-input" />
        </div>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" className="h-9 rounded-full text-muted-foreground" onClick={reset} data-testid="map-filter-reset"><X /> Nullstill</Button>
        )}
        {summary && (
          <span className="ml-1 text-xs tabular-nums text-muted-foreground" data-testid="map-summary">
            {summary.located} maskin{summary.located === 1 ? "" : "er"}
            {summary.unlocated ? ` · ${summary.unlocated} utan plassering` : ""}
          </span>
        )}
      </div>

      {/* Mobil filter-drawer */}
      <Drawer open={filterOpen} onOpenChange={onFilterOpen}>
        <DrawerContent data-testid="map-filter-drawer">
          <DrawerHeader>
            <DrawerTitle>Filter</DrawerTitle>
            <DrawerDescription>Avgrens kva som blir vist på kartet.</DrawerDescription>
          </DrawerHeader>
          <DrawerBody className="space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={filters.q} onChange={(e) => onChange({ q: e.target.value })} placeholder="Søk maskin, kunde, adresse …" className="h-11 pl-9" data-testid="map-search-input-mobile" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</Label>
              <StatusChips value={filters.status} onChange={(status) => onChange({ status })} testid="map-status-mobile" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fristtype</Label>
              <KindChips value={filters.kind} onChange={(kind) => onChange({ kind })} testid="map-kind-mobile" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kunde</Label>
              <CustomerSelect customers={customers} value={filters.customer} onChange={(customer) => onChange({ customer })} className="h-11 w-full rounded-lg text-sm" />
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-row">
            <Button variant="outline" className="h-12 flex-1" onClick={reset} data-testid="map-filter-reset-mobile">Nullstill</Button>
            <Button className="h-12 flex-1" onClick={() => onFilterOpen(false)} data-testid="map-filter-done">Vis {summary?.located ?? ""} maskiner</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
